import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { useLiveEvents } from "../lib/useLiveEvents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Ctx = createContext(null);

/** Public service/feature availability (from Superadmin → Service & Feature Controls). Live-updated; fail-closed while unknown. */
export function FeatureProvider({ children }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => axios.get(`${API}/features`).then((r) => setD(r.data)).catch(() => setD((x) => x || { loaded: false, features: {} })), []);
  useEffect(() => { load(); }, [load]);
  useLiveEvents(["catalog"], (t, ev) => { if (t === "poll" || ev?.collection === "feature_controls") load(); });
  const value = useMemo(() => {
    const feats = d?.features || {};
    const get = (key) => feats[key] || (d?.loaded ? { enabled: true, mode: "on" } : { enabled: false, mode: "hidden", reason: "loading" });
    const forPath = (path) => { // most specific (longest) matching route wins
      let best = null;
      Object.entries(feats).forEach(([key, f]) => (f.routes || []).forEach((r) => { if ((path === r || path.startsWith(r)) && (!best || r.length > best.route.length)) best = { key, route: r, ...f }; }));
      return best;
    };
    const hiddenPath = (path) => { const m = forPath(path); return m && !m.enabled && m.mode !== "coming_soon"; };
    return { ready: !!d, loaded: !!d?.loaded, features: feats, get, forPath, hiddenPath, on: (key) => get(key).enabled, reload: load };
  }, [d, load]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
export const useFeatures = () => useContext(Ctx) || { ready: false, loaded: false, features: {}, get: () => ({ enabled: true }), forPath: () => null, hiddenPath: () => false, on: () => true, reload: () => {} };

/** Wraps public routes: hidden → "not available" page; coming soon → message page (no booking/purchase). Account/staff/admin routes are never gated. */
export function FeatureRouteGuard({ children }) {
  const { pathname } = useLocation(); const { ready, forPath } = useFeatures();
  if (!ready || /^\/(account|staff|admin|doctor-portal|corporate-portal|api|login|register|my-health)/.test(pathname)) return children;
  const m = forPath(pathname);
  if (!m || m.enabled) return children;
  return <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="feature-unavailable" data-mode={m.mode} data-feature={m.key}>
    <div className="max-w-md w-full bg-white border border-gray-200 rounded-3xl p-8 text-center space-y-3">
      <p className="text-[11px] uppercase tracking-widest font-bold text-[#3B8271]">{m.mode === "coming_soon" ? "Coming soon" : "Not available"}</p>
      <h1 className="text-2xl font-bold text-[#0D3B2E]">{m.label}</h1>
      <p className="text-sm text-gray-600" data-testid="feature-unavailable-message">{m.mode === "coming_soon" ? (m.message || "We're getting this service ready. Bookings and purchases will open soon.") : "This service is not available at the moment."}</p>
      <p className="text-xs text-gray-500">Existing bookings, orders and reports remain available in your account.</p>
      <div className="flex justify-center gap-2 pt-2"><Link to="/" className="px-4 py-2 rounded-xl bg-[#0D3B2E] text-white text-sm font-bold" data-testid="feature-unavailable-home">Back to home</Link><Link to="/contact" className="px-4 py-2 rounded-xl border text-sm font-bold" data-testid="feature-unavailable-contact">Contact us</Link></div>
    </div></div>;
}
